<!--
  Transaction Filters Component
  Advanced filtering interface for transaction dashboard
  @component TransactionFilters
-->
<template>
  <div class="transaction-filters">
    <div class="row q-gutter-md q-mb-md">
      <div class="col-12">
        <h6 class="q-ma-none q-mb-md">{{ t('composites.TransactionProcessingDashboard.filters.title') }}</h6>
      </div>
    </div>

    <div class="row q-gutter-md">
      <!-- Search Input -->
      <div class="col-12 col-md-6 col-lg-3">
        <q-input
          v-model="localFilters.search"
          :label="t('composites.TransactionProcessingDashboard.filters.search')"
          :placeholder="t('composites.TransactionProcessingDashboard.filters.search_placeholder')"
          outlined
          dense
          clearable
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedSearch" />
          </template>
        </q-input>
      </div>

      <!-- Status Filter -->
      <div class="col-12 col-md-6 col-lg-3">
        <q-select
          v-model="localFilters.status"
          :options="statusOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.status')"
          multiple
          outlined
          dense
          clearable
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedFilterList" />
          </template>
        </q-select>
      </div>

      <!-- Transmission Status Filter -->
      <div class="col-12 col-md-6 col-lg-3">
        <q-select
          v-model="localFilters.transmissionStatus"
          :options="transmissionStatusOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.transmission_status')"
          multiple
          outlined
          dense
          clearable
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedSync" />
          </template>
        </q-select>
      </div>

      <!-- Transmission Type Filter -->
      <div class="col-12 col-md-6 col-lg-3">
        <q-select
          v-model="localFilters.transmissionType"
          :options="transmissionTypeOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.transmission_type')"
          multiple
          outlined
          dense
          clearable
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedCategory" />
          </template>
        </q-select>
      </div>
    </div>

    <div class="row q-gutter-md q-mt-md">
      <!-- Date Range -->
      <div class="col-12 col-md-6 col-lg-4">
        <q-input
          v-model="localFilters.dateRange.start"
          :label="t('composites.TransactionProcessingDashboard.filters.start_date')"
          type="date"
          outlined
          dense
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedCalendarToday" />
          </template>
        </q-input>
      </div>

      <div class="col-12 col-md-6 col-lg-4">
        <q-input
          v-model="localFilters.dateRange.end"
          :label="t('composites.TransactionProcessingDashboard.filters.end_date')"
          type="date"
          outlined
          dense
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedCalendarToday" />
          </template>
        </q-input>
      </div>

      <!-- Business Partner Filter -->
      <div class="col-12 col-lg-4">
        <q-select
          v-model="localFilters.businessPartnerId"
          :options="businessPartnerOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.business_partner')"
          multiple
          outlined
          dense
          clearable
          emit-value
          map-options
          use-input
          input-debounce="300"
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedPerson" />
          </template>
        </q-select>
      </div>
    </div>

    <div class="row q-gutter-md q-mt-md">
      <!-- Has Errors Toggle -->
      <div class="col-12 col-md-6 col-lg-3">
        <q-select
          v-model="localFilters.hasErrors"
          :options="errorFilterOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.error_status')"
          outlined
          dense
          clearable
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedError" />
          </template>
        </q-select>
      </div>

      <!-- Sort Options -->
      <div class="col-12 col-md-3 col-lg-2">
        <q-select
          v-model="localFilters.sortBy"
          :options="sortByOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.sort_by')"
          outlined
          dense
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedSort" />
          </template>
        </q-select>
      </div>

      <div class="col-12 col-md-3 col-lg-2">
        <q-select
          v-model="localFilters.sortOrder"
          :options="sortOrderOptions"
          :label="t('composites.TransactionProcessingDashboard.filters.sort_order')"
          outlined
          dense
          emit-value
          map-options
          :loading="loading"
          @update:model-value="handleFilterChange">
          <template #prepend>
            <q-icon :name="symRoundedSwapVert" />
          </template>
        </q-select>
      </div>

      <!-- Action Buttons -->
      <div class="col-12 col-lg-5">
        <div class="row q-gutter-sm justify-end">
          <q-btn
            :icon="symRoundedRefresh"
            :label="t('composites.TransactionProcessingDashboard.filters.reset')"
            color="grey"
            outline
            no-caps
            @click="resetFilters" />
          <q-btn
            :icon="symRoundedFilterList"
            :label="t('composites.TransactionProcessingDashboard.filters.apply')"
            color="primary"
            no-caps
            @click="applyFilters" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedCalendarToday,
  symRoundedCategory,
  symRoundedError,
  symRoundedFilterList,
  symRoundedPerson,
  symRoundedRefresh,
  symRoundedSearch,
  symRoundedSort,
  symRoundedSwapVert,
  symRoundedSync,
} from '@quasar/extras/material-symbols-rounded'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { BusinessPartner, DashboardFilters, TransactionFiltersEmits, TransactionFiltersProps } from '../types'

const props = withDefaults(defineProps<TransactionFiltersProps>(), {
  businessPartners: () => [],
  loading: false,
})

const emit = defineEmits<TransactionFiltersEmits>()

const { t } = useI18n()

// Local filter state
const localFilters = ref<DashboardFilters>({ ...props.modelValue })

// Status options
const statusOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.status.ready_to_invoice'), value: 'ready_to_invoice' },
  { label: t('composites.TransactionProcessingDashboard.status.ready_to_send'), value: 'ready_to_send' },
  { label: t('composites.TransactionProcessingDashboard.status.need_check'), value: 'need_check' },
  { label: t('composites.TransactionProcessingDashboard.status.sent'), value: 'sent' },
  { label: t('composites.TransactionProcessingDashboard.status.cancelled'), value: 'cancelled' },
  { label: t('composites.TransactionProcessingDashboard.status.draft'), value: 'draft' },
  { label: t('composites.TransactionProcessingDashboard.status.processing'), value: 'processing' },
  { label: t('composites.TransactionProcessingDashboard.status.failed'), value: 'failed' },
  { label: t('composites.TransactionProcessingDashboard.status.completed'), value: 'completed' },
])

// Transmission status options
const transmissionStatusOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.transmission_status.pending'), value: 'pending' },
  { label: t('composites.TransactionProcessingDashboard.transmission_status.success'), value: 'success' },
  { label: t('composites.TransactionProcessingDashboard.transmission_status.failed'), value: 'failed' },
  { label: t('composites.TransactionProcessingDashboard.transmission_status.cancelled'), value: 'cancelled' },
  { label: t('composites.TransactionProcessingDashboard.transmission_status.processing'), value: 'processing' },
])

// Transmission type options
const transmissionTypeOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.transmission_type.external_invoice'), value: 'external_invoice' },
  { label: t('composites.TransactionProcessingDashboard.transmission_type.accounting_batch'), value: 'accounting_batch' },
  { label: t('composites.TransactionProcessingDashboard.transmission_type.external_correction'), value: 'external_correction' },
  { label: t('composites.TransactionProcessingDashboard.transmission_type.internal_correction'), value: 'internal_correction' },
  { label: t('composites.TransactionProcessingDashboard.transmission_type.external_cancellation'), value: 'external_cancellation' },
  { label: t('composites.TransactionProcessingDashboard.transmission_type.internal_cancellation'), value: 'internal_cancellation' },
])

// Business partner options
const businessPartnerOptions = computed(() =>
  props.businessPartners.map((partner: BusinessPartner) => ({
    label: partner.legal_name,
    value: partner.id,
  })),
)

// Error filter options
const errorFilterOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.filters.with_errors'), value: true },
  { label: t('composites.TransactionProcessingDashboard.filters.without_errors'), value: false },
])

// Sort by options
const sortByOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.filters.sort_created_at'), value: 'created_at' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_updated_at'), value: 'updated_at' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_transaction_date'), value: 'transaction_date' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_total_amount'), value: 'amount_total' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_status'), value: 'status' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_number'), value: 'number' },
])

// Sort order options
const sortOrderOptions = computed(() => [
  { label: t('composites.TransactionProcessingDashboard.filters.sort_desc'), value: 'desc' },
  { label: t('composites.TransactionProcessingDashboard.filters.sort_asc'), value: 'asc' },
])

/**
 * Handle filter changes
 */
const handleFilterChange = (): void => {
  emit('update:model-value', { ...localFilters.value })
}

/**
 * Reset all filters to default values
 */
const resetFilters = (): void => {
  localFilters.value = {
    status: undefined,
    transmissionStatus: undefined,
    transmissionType: undefined,
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0],
    },
    businessPartnerId: undefined,
    search: '',
    hasErrors: undefined,
    sortBy: 'created_at',
    sortOrder: 'desc',
  }
  emit('reset')
  handleFilterChange()
}

/**
 * Apply current filters
 */
const applyFilters = (): void => {
  emit('apply', { ...localFilters.value })
}

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    localFilters.value = { ...newValue }
  },
  { deep: true },
)
</script>

<style lang="scss" scoped>
.transaction-filters {
  .q-field {
    margin-bottom: 0;
  }
}
</style>
